import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Flame, Footprints } from 'lucide-react'
import { customerApi } from '../services/api'
import { useRestaurant } from '../contexts/RestaurantContext'
import { useLanguage } from '../contexts/LanguageContext'
import AllergenIcons from '../components/AllergenIcons'
import { getLocalizedName, getLocalizedDescription } from '../utils/localization'

interface MenuItem {
  id: string
  name: string
  description: string
  base_price: number
  code?: string
  image?: string
  is_popular: boolean
  category_id: string
  calories?: number
  steps_to_burn?: number
  allergens?: string[]
}

export default function CategoryView() {
  const { categoryId } = useParams<{ categoryId: string }>()
  const navigate = useNavigate()
  const { formatPrice, allergenMap } = useRestaurant()
  const { language } = useLanguage()
  const [items, setItems] = useState<MenuItem[]>([])
  const [categoryName, setCategoryName] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (categoryId) {
      loadCategoryItems()
    }
  }, [categoryId])

  const loadCategoryItems = async () => {
    try {
      setLoading(true)
      const [menuResponse, categoriesResponse] = await Promise.all([
        customerApi.getMenu(),
        customerApi.getCategories()
      ])

      if (menuResponse.success && menuResponse.data && Array.isArray(menuResponse.data)) {
        const categoryItems = menuResponse.data
          .filter((item: MenuItem) => item.category_id === categoryId)
          .sort((a: MenuItem, b: MenuItem) => {
            const codeA = (a as any).code ?? ''
            const codeB = (b as any).code ?? ''
            if (codeA !== codeB) return codeA.localeCompare(codeB, undefined, { numeric: true })
            return (a.name ?? '').localeCompare(b.name ?? '')
          })
        setItems(categoryItems)
      }

      if (categoriesResponse.success && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
        const category = categoriesResponse.data.find((c: any) => c.id === categoryId)
        if (category) {
          setCategoryName(getLocalizedName(category.name, category.name_ar, language))
        }
      }
    } catch (error) {
      console.error('Error loading category items:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-700 hover:text-amber-500 shrink-0"
            >
              <ArrowLeft className="w-5 h-5 rtl:scale-x-[-1]" />
              <span>Back</span>
            </button>
            <h1 className="flex-1 text-center text-lg font-semibold text-gray-900 truncate">
              {categoryName || 'Category'}
            </h1>
            <div className="w-14 shrink-0" aria-hidden />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          {categoryName || 'Category'}
        </h1>

        <div className="grid grid-cols-2 gap-4">
          {items.map(item => (
            <Link
              key={item.id}
              to={`/item/${item.id}`}
              className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              {item.image && (
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-32 object-cover"
                />
              )}
              <div className="p-3">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {item.code && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium font-mono bg-amber-200 text-amber-900">
                      {item.code}
                    </span>
                  )}
                  <h3 className="font-semibold text-gray-900">{getLocalizedName(item.name, (item as any).name_ar, language)}</h3>
                </div>
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">{getLocalizedDescription(item.description, (item as any).description_ar, language)}</p>
                <p className="text-lg font-bold text-amber-500">{formatPrice(item.base_price)}</p>
                {(item.calories != null || item.steps_to_burn != null) && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-gray-500">
                    {item.calories != null && (
                      <span className="flex items-center">
                        <Flame className="w-3.5 h-3.5 mr-1 shrink-0 text-orange-500" />
                        {item.calories} cal
                      </span>
                    )}
                    {item.steps_to_burn != null && (
                      <span className="flex items-center">
                        <Footprints className="w-3.5 h-3.5 mr-1 shrink-0 text-blue-500" />
                        {item.steps_to_burn} steps
                      </span>
                    )}
                  </div>
                )}
                {item.allergens && item.allergens.length > 0 && (
                  <div className="mt-1.5">
                    <AllergenIcons allergenIds={item.allergens} allergenMap={allergenMap} size={18} />
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>

        {items.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No items in this category</p>
          </div>
        )}
      </div>
    </div>
  )
}

